/**
 * Developer dashboard. Dev-gated landing page with three sections:
 *
 *   1. Stats — at-a-glance counts (users, characters, reports + breakdowns)
 *   2. Bug Reports — full filter / grid / export (the previous DevReportsPage,
 *      now embedded as a section here)
 *   3. Insights — 30-day report sparkline, top tags, top views, link to
 *      the Convex dashboard for raw infra metrics
 *
 * Themed to match `MyReportsPage` so the two pages feel like siblings —
 * shared page-level styles are exported from MyReportsPage and reused here.
 *
 * Future Phase 2 additions (low effort once data exists):
 *   - Real "active users" via session heartbeats (needs `users.lastSeenAt`
 *     or a `userActivity` table, plus a periodic ping from the client).
 *   - Time-spent-in-app per user (needs the same heartbeat infrastructure).
 *   - Cohort retention (signed up week N → still active week N+M).
 *   - Slack / email digest of dashboard summary on a schedule.
 */

import React from 'react';
import {
  useAllBugReports,
  useIsDev,
  useDevDashboardStats,
  useBugReporterUsage,
  useSystemsUsageByDm,
  useUserActivity,
  useViewCounters,
  useDashboardInsights,
  useFeatureFlags,
  useSetFeatureFlag,
} from '../hooks/useBugReports.js';
import ReportThread from './ReportThread.jsx';
import ExportPanel from './ExportPanel.jsx';
import {
  PAGE_WRAP_STYLE,
  PAGE_INNER_STYLE,
  PAGE_TITLE_STYLE,
  PAGE_CARD_STYLE,
  SECTION_DIVIDER_STYLE,
  SECTION_HEADING_STYLE,
  SECTION_RULE_STYLE,
} from './MyReportsPage.jsx';

const STATUS_INFO = {
  open:      { label: 'Open',      color: '#ff6961' },
  addressed: { label: 'Addressed', color: '#9b59b6' },
  resolved:  { label: 'Resolved',  color: '#27c95a' },
};

const TAG_COLORS = {
  bug: '#ff6961',
  critical: '#ff3b30',
  feature: '#4a90d9',
  suggestion: '#c9a227',
  interface: '#9b59b6',
  ui: '#9b59b6',
  question: '#888',
};

const INFRA_TOOL_LINKS = [
  {
    label: 'Convex deployment',
    href: 'https://dashboard.convex.dev/t/carolinemkuzio/dnd-tracker/energetic-coyote-203',
    description: 'Functions, logs, tables, and deployment state.',
  },
  {
    label: 'Convex usage',
    href: 'https://dashboard.convex.dev/t/carolinemkuzio/settings/usage',
    description: 'Usage, bandwidth, storage, and billing limits.',
  },
  {
    label: 'Convex database docs',
    href: 'https://docs.convex.dev/database',
    description: 'Schema, indexes, queries, and mutation reference.',
  },
  {
    label: 'Vercel deployments',
    href: 'https://vercel.com/carrie-kuzios-projects/~/deployments',
    description: 'Frontend deploy history and deployment status.',
  },
  {
    label: 'GitHub repository',
    href: 'https://github.com/carriekuzio/dnd-tracker',
    description: 'Source code, branches, commits, and pull requests.',
  },
];

const VIEW_LABELS = {
  select: { label: 'Character Select / Login', description: 'Initial character selection and login screen.' },
  home: { label: 'Character Home', description: 'Player character home dashboard.' },
  dm: { label: 'DM Dashboard', description: 'Main DM campaign dashboard.' },
  'dev-dashboard': { label: 'Developer Dashboard', description: 'Developer analytics and controls.' },
  'bug-reports': { label: 'Player Bug Reports', description: 'Player-facing report list.' },
  'dev-reports': { label: 'All Bug Reports', description: 'Developer report triage browser.' },
  npcs: { label: 'NPCs / Library', description: 'NPC and library management.' },
  shop: { label: 'Shop', description: 'Shop browser and shop management.' },
  crafting: { label: 'Crafting', description: 'Crafting table and recipe tools.' },
  combat: { label: 'Combat Tracker', description: 'Combat encounter view.' },
  journal: { label: 'Party Journal', description: 'Party journal view.' },
  loot: { label: 'Loot', description: 'Loot and encounter rewards.' },
  travel: { label: 'Travel Logs', description: 'Travel and survival log view.' },
  timeline: { label: 'Audit Timeline', description: 'DM audit and snapshot timeline.' },
  library: { label: 'Library', description: 'General library view.' },
  messages: { label: 'Messages', description: 'Party and DM messaging.' },
};

const humanizeViewKey = (view) => String(view || 'unknown')
  .split(/[-_]/)
  .filter(Boolean)
  .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ') || 'Unknown View';

const getViewLabelMeta = (view) => VIEW_LABELS[view] || {
  label: humanizeViewKey(view),
  description: `Raw view key: ${view || 'unknown'}`,
};

const formatViewLabel = (view) => getViewLabelMeta(view).label;

const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

const truncateTitle = (title, words = 10) => {
  if (!title) return '(untitled)';
  const arr = title.split(/\s+/);
  if (arr.length <= words) return title;
  return arr.slice(0, words).join(' ') + '…';
};

const renderImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return null;
  if (typeof parsed.importanceRaw === 'number' && typeof parsed.importanceScale === 'number') {
    return `★ ${parsed.importanceRaw}/${parsed.importanceScale}`;
  }
  if (typeof parsed.importance === 'number') {
    return `★ ${parsed.importance}/10`;
  }
  return null;
};

const effectiveImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return 11;
  return typeof parsed.importance === 'number' ? parsed.importance : 0;
};

// --- Stat cards --------------------------------------------------------

function StatCard({ label, value, sublabel, accent = '#c9a227', children }) {
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 16,
      borderLeft: `4px solid ${accent}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minHeight: 110,
    }}>
      <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.8rem',
        color: accent,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontWeight: 700,
        lineHeight: 1,
      }}>
        {value}
      </div>
      {sublabel && (
        <div style={{ fontSize: '0.66rem', color: '#888' }}>{sublabel}</div>
      )}
      {children}
    </div>
  );
}

function StatusBreakdown({ byStatus }) {
  const total = (byStatus.open || 0) + (byStatus.addressed || 0) + (byStatus.resolved || 0) || 1;
  const segments = [
    { id: 'open',      color: STATUS_INFO.open.color,      count: byStatus.open || 0 },
    { id: 'addressed', color: STATUS_INFO.addressed.color, count: byStatus.addressed || 0 },
    { id: 'resolved',  color: STATUS_INFO.resolved.color,  count: byStatus.resolved || 0 },
  ];
  return (
    <div style={{ marginTop: 'auto' }}>
      <div style={{
        display: 'flex',
        height: 6,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.06)',
      }}>
        {segments.map(s => s.count > 0 && (
          <div
            key={s.id}
            title={`${s.id}: ${s.count}`}
            style={{ flexGrow: s.count / total, background: s.color }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: '0.6rem', color: '#888' }}>
        {segments.map(s => (
          <span key={s.id}>
            <span style={{ color: s.color }}>●</span> {s.id} {s.count}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatsGrid({ stats, insights }) {
  if (stats === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading insights…</div>;
  }
  if (stats === null) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 12,
      marginBottom: 8,
    }}>
      <StatCard
        label="Reports"
        value={stats.reports.total}
        sublabel={`${stats.reports.last7d} new in last 7 days`}
        accent="#c9a227"
      >
        <StatusBreakdown byStatus={stats.reports.byStatus} />
      </StatCard>
      <StatCard
        label="Users"
        value={stats.users.total}
        sublabel={`${stats.users.activeReportersLast7d} reported in last 7 days`}
        accent="#4a90d9"
      />
      <StatCard
        label="Characters"
        value={stats.characters.total}
        accent="#9b59b6"
      />
      <StatCard
        label="Avg importance"
        value={stats.reports.averageImportance ?? '—'}
        sublabel="rated reports (1-10 normalized)"
        accent="#27c95a"
      />
      <StatCard
        label="Avg criticality"
        value={insights?.averageCriticality != null ? insights.averageCriticality : '—'}
        sublabel="bug + critical reports"
        accent="#ff3b30"
      />
    </div>
  );
}

// --- Insights section --------------------------------------------------

function ReportsPerDayChart({ perDay }) {
  if (!perDay || perDay.length === 0) return null;
  const maxCount = Math.max(1, ...perDay.map(d => d.count));
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 16,
    }}>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 8 }}>
        📈 Reports — last 30 days
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        height: 80,
        padding: '6px 0',
      }}>
        {perDay.map(d => {
          const h = d.count === 0 ? 2 : Math.max(4, Math.round((d.count / maxCount) * 70));
          const isToday = d === perDay[perDay.length - 1];
          return (
            <div
              key={d.date}
              title={`${d.date}: ${d.count} report${d.count === 1 ? '' : 's'}`}
              style={{
                flex: 1,
                height: h,
                background: d.count === 0
                  ? 'rgba(255,255,255,0.08)'
                  : isToday
                    ? 'linear-gradient(180deg, #ffd700, #c9a227)'
                    : 'linear-gradient(180deg, #c9a227, #8b6914)',
                borderRadius: 2,
                minWidth: 4,
              }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.55rem', color: '#666', marginTop: 4 }}>
        <span>{perDay[0]?.date}</span>
        <span>today</span>
      </div>
    </div>
  );
}

function HorizontalBars({ title, items, colorFor }) {
  if (!items || items.length === 0) return null;
  const max = Math.max(1, ...items.map(it => it[1]));
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 16,
    }}>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(([key, count]) => {
          const c = colorFor ? colorFor(key) : '#c9a227';
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem' }}>
              <span style={{ width: 90, color: '#b0a48a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {key}
              </span>
              <div style={{
                flex: 1,
                height: 12,
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 3,
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.round((count / max) * 100)}%`,
                  background: `linear-gradient(90deg, ${c}66, ${c})`,
                }} />
              </div>
              <span style={{ width: 24, textAlign: 'right', color: '#c9a227', fontWeight: 600 }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InsightsSection({ stats }) {
  if (!stats) return null;
  const tags = (stats.reports.topTags || []).map(t => [t.tag, t.count]);
  const views = (stats.reports.topViews || []).map(v => [formatViewLabel(v.view), v.count]);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 12,
      alignItems: 'start',
    }}>
      <ReportsPerDayChart perDay={stats.reports.perDayLast30d} />
      <HorizontalBars
        title="🏷️ Top tags"
        items={tags}
        colorFor={tag => TAG_COLORS[tag] || '#c9a227'}
      />
      <HorizontalBars
        title="📍 Top views (where reports come from)"
        items={views}
      />
      <div style={{
        ...PAGE_CARD_STYLE,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}>
        <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600 }}>
          🔧 External tools
        </div>
        <a
          href="https://dashboard.convex.dev"
          target="_blank"
          rel="noreferrer"
          style={{
            padding: '6px 12px',
            background: 'rgba(74,144,217,0.12)',
            border: '1px solid rgba(74,144,217,0.4)',
            borderRadius: 5,
            color: '#4a90d9',
            textDecoration: 'none',
            fontSize: '0.7rem',
            display: 'inline-block',
            width: 'fit-content',
          }}
        >
          Convex dashboard →
        </a>
        <div style={{ fontSize: '0.58rem', color: '#666', lineHeight: 1.3 }}>
          Raw infra metrics: function calls, bandwidth, storage, real-time function logs.
        </div>
      </div>
    </div>
  );
}

// --- Feature-flag toggles ------------------------------------------------

function FeatureFlagToggle({ flagKey, flag, onToggle, busy, disabledReason = null }) {
  const enabled = !!flag.enabled;
  const disabled = busy || !!disabledReason;
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 14,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      borderLeft: `4px solid ${enabled ? '#27c95a' : '#666'}`,
      opacity: busy ? 0.6 : 1,
      transition: 'opacity 120ms ease-out, border-color 200ms ease-out',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.86rem',
          fontWeight: 600,
          color: enabled ? '#e8d5b7' : '#888',
          fontFamily: 'Georgia, "Times New Roman", serif',
          marginBottom: 2,
        }}>
          {flag.label}
        </div>
        <div style={{ fontSize: '0.65rem', color: '#888', lineHeight: 1.4 }}>
          {flag.description}
        </div>
        {flag.disabledBy && (
          <div style={{ fontSize: '0.6rem', color: '#c9a227', lineHeight: 1.4, marginTop: 4 }}>
            Disabled by {flag.disabledBy.replace(/^feature\./, '')}
          </div>
        )}
        {disabledReason && (
          <div style={{ fontSize: '0.6rem', color: '#c9a227', lineHeight: 1.4, marginTop: 4 }}>
            {disabledReason}
          </div>
        )}
        {flag.updatedAt && (
          <div style={{ fontSize: '0.55rem', color: '#666', fontStyle: 'italic', marginTop: 4 }}>
            Last changed {new Date(flag.updatedAt).toLocaleString()}
          </div>
        )}
      </div>
      <button
        onClick={() => !disabled && onToggle(flagKey, !enabled)}
        disabled={disabled}
        style={{
          position: 'relative',
          width: 44,
          height: 24,
          borderRadius: 12,
          border: `1px solid ${enabled ? '#27c95a' : 'rgba(255,255,255,0.2)'}`,
          background: enabled ? 'rgba(39,201,90,0.25)' : 'rgba(255,255,255,0.05)',
          cursor: busy ? 'wait' : (disabledReason ? 'not-allowed' : 'pointer'),
          padding: 0,
          flexShrink: 0,
          marginTop: 2,
          transition: 'background 160ms ease-out, border-color 160ms ease-out',
        }}
        title={disabledReason || (enabled ? 'Disable feature' : 'Enable feature')}
      >
        <span style={{
          position: 'absolute',
          top: 2,
          left: enabled ? 22 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: enabled ? '#27c95a' : '#888',
          transition: 'left 160ms ease-out, background 160ms ease-out',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        }} />
      </button>
    </div>
  );
}

function FeatureFlagsSection() {
  const flags = useFeatureFlags();
  const setFlag = useSetFeatureFlag();
  const [busyKey, setBusyKey] = React.useState(null);
  const [error, setError] = React.useState(null);

  if (flags === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading flags…</div>;
  }

  const handleToggle = async (key, enabled) => {
    setBusyKey(key);
    setError(null);
    try {
      await setFlag({ key, enabled });
    } catch (err) {
      setError(err?.message || 'Failed to update flag');
    } finally {
      setBusyKey(null);
    }
  };

  const entries = Object.entries(flags);
  const craftingEnabled = flags['feature.crafting']?.enabled !== false;
  return (
    <div>
      <div style={{
        marginBottom: 10,
        padding: '8px 12px',
        background: 'rgba(74,144,217,0.06)',
        border: '1px solid rgba(74,144,217,0.2)',
        borderRadius: 6,
        fontSize: '0.7rem',
        color: '#b0a48a',
      }}>
        System-wide kill switches. Disabling a feature hides it for every user.
        Re-enable any time — no data loss, surfaces just hide and unhide.
        Turning Crafting off also disables Professions.
      </div>
      {error && (
        <div style={{
          marginBottom: 10,
          padding: '8px 12px',
          background: 'rgba(255,105,97,0.1)',
          border: '1px solid #ff6961',
          borderRadius: 6,
          color: '#ff6961',
          fontSize: '0.7rem',
        }}>
          {error}
        </div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 10,
      }}>
        {entries.map(([key, flag]) => (
          <FeatureFlagToggle
            key={key}
            flagKey={key}
            flag={flag}
            busy={busyKey === key}
            onToggle={handleToggle}
            disabledReason={key === 'feature.professions' && !craftingEnabled ? 'Enable Crafting before enabling Professions.' : null}
          />
        ))}
      </div>
    </div>
  );
}

// --- Bug-reporter usage insights ----------------------------------------

const USAGE_FEATURES = [
  { key: 'withDescription',   label: 'Description',      color: '#4a90d9' },
  { key: 'withScreenshots',   label: 'Screenshots',      color: '#9b59b6' },
  { key: 'withElements',      label: 'Element select',   color: '#27c95a' },
  { key: 'withActionLogging', label: 'Action logging',   color: '#c9a227' },
];

function UsagePctBar({ count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem' }}>
      <div style={{ flex: 1, height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: `linear-gradient(90deg, ${color}66, ${color})`,
          transition: 'width 200ms ease-out',
        }} />
      </div>
      <span style={{ width: 50, textAlign: 'right', color: '#b0a48a', fontFamily: 'monospace', fontSize: '0.65rem' }}>
        {count}/{total}
      </span>
      <span style={{ width: 36, textAlign: 'right', color, fontWeight: 600, fontSize: '0.65rem' }}>
        {pct}%
      </span>
    </div>
  );
}

function ReporterUsageCard({ row, isAggregate }) {
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      border: isAggregate ? '1px solid rgba(201,162,39,0.4)' : PAGE_CARD_STYLE.border,
      background: isAggregate
        ? 'linear-gradient(180deg, rgba(201,162,39,0.06), rgba(201,162,39,0.02))'
        : PAGE_CARD_STYLE.background,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.92rem',
          fontWeight: 600,
          color: isAggregate ? '#c9a227' : '#e8d5b7',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>
          {isAggregate ? '∑ All reporters' : row.name}
        </span>
        <span style={{ fontSize: '0.6rem', color: '#888' }}>
          {row.total} report{row.total === 1 ? '' : 's'}
        </span>
        {!isAggregate && row.lastReportAt > 0 && (
          <span style={{ fontSize: '0.55rem', color: '#666', marginLeft: 'auto' }}>
            last {new Date(row.lastReportAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {USAGE_FEATURES.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 100, fontSize: '0.65rem', color: f.color, fontWeight: 500 }}>
              {f.label}
            </span>
            <div style={{ flex: 1 }}>
              <UsagePctBar count={row[f.key]} total={row.total} color={f.color} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BugReporterUsageSection() {
  const data = useBugReporterUsage();
  if (data === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading reporter usage…</div>;
  }
  if (data === null || (data.aggregate.total === 0)) {
    return (
      <div style={{
        padding: 24,
        textAlign: 'center',
        color: '#666',
        background: 'rgba(255,255,255,0.02)',
        border: '1px dashed rgba(201,162,39,0.2)',
        borderRadius: 8,
        fontSize: '0.78rem',
      }}>
        No reports yet — feature usage breakdown will appear here once reports come in.
      </div>
    );
  }
  return (
    <div>
      <div style={{
        marginBottom: 10,
        padding: '8px 12px',
        background: 'rgba(74,144,217,0.06)',
        border: '1px solid rgba(74,144,217,0.2)',
        borderRadius: 6,
        fontSize: '0.65rem',
        color: '#888',
      }}>
        Per-reporter view. Action logging = the user actively recorded a repro path
        (clicked "Log steps to replicate") rather than relying on the auto-attached audit slice.
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 12,
      }}>
        <ReporterUsageCard row={data.aggregate} isAggregate />
        {data.perReporter.map((row, i) => (
          <ReporterUsageCard key={i} row={row} />
        ))}
      </div>
    </div>
  );
}

// --- Systems usage by DM ------------------------------------------------

function SystemsUsageDmCard({ row }) {
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      borderLeft: '4px solid #9b59b6',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#e8d5b7',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>
          {row.name}
        </span>
        {row.lastTouchedAt > 0 && (
          <span style={{ fontSize: '0.55rem', color: '#666', marginLeft: 'auto' }}>
            last touched {new Date(row.lastTouchedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        marginTop: 4,
      }}>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Chars</div>
          <div style={{ fontSize: '1.15rem', color: '#9b59b6', fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.charsManaged}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Instances</div>
          <div style={{ fontSize: '1.15rem', color: '#9b59b6', fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.systemInstances}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Unique</div>
          <div style={{ fontSize: '1.15rem', color: '#9b59b6', fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.uniqueSystems}</div>
        </div>
      </div>
    </div>
  );
}

function SystemsUsageSection() {
  const data = useSystemsUsageByDm();
  if (data === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading systems usage…</div>;
  }
  if (data === null) return null;
  const { aggregate, perDm } = data;
  return (
    <div>
      <div style={{
        marginBottom: 10,
        padding: '8px 12px',
        background: 'rgba(155,89,182,0.06)',
        border: '1px solid rgba(155,89,182,0.25)',
        borderRadius: 6,
        fontSize: '0.65rem',
        color: '#888',
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <span><span style={{ color: '#9b59b6', fontWeight: 600 }}>{aggregate.totalTemplates}</span> templates in library</span>
        <span><span style={{ color: '#9b59b6', fontWeight: 600 }}>{aggregate.totalInstances}</span> instances applied across <span style={{ color: '#9b59b6', fontWeight: 600 }}>{aggregate.totalDms}</span> DM{aggregate.totalDms === 1 ? '' : 's'}</span>
        <span><span style={{ color: '#9b59b6', fontWeight: 600 }}>{aggregate.dmSystemEventsLast7d}</span> system events from DMs in last 7d</span>
      </div>
      {perDm.length === 0 ? (
        <div style={{
          padding: 24,
          textAlign: 'center',
          color: '#666',
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(155,89,182,0.2)',
          borderRadius: 8,
          fontSize: '0.78rem',
        }}>
          No DMs have applied any systems yet.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 12,
        }}>
          {perDm.map((row, i) => (
            <SystemsUsageDmCard key={i} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Activity insights (audit-derived) ----------------------------------

// Audit category palette. 2026-04-30 v3: matches the reporter-usage
// chart aesthetic (saturated mid-bright hues) and uses singular/plural
// aliases so categories that App.js emits as either form ('buff' vs
// 'buffs', 'message' vs 'messages') always map to the same color.
//
// Hue assignments are intuitive — combat=red, food=green, magic=indigo,
// gold=currency — so the agent / dev can pattern-match without reading
// the legend every time.
const CATEGORY_COLORS = {
  // Combat & danger — red family
  combat:        '#e74c3c',
  condition:     '#e74c3c',
  concentration: '#c0392b',
  // Buffs / power-up — warm gold-yellow (matches "action logging" gold)
  buff:          '#f1c40f',
  buffs:         '#f1c40f',
  xp:            '#f39c12',
  // Debuffs — pink-magenta (warm but negative)
  debuff:        '#e91e63',
  diseases:      '#d81b60',
  // Inventory / loot — orange
  inventory:     '#e67e22',
  items:         '#e67e22',
  loot:          '#d35400',
  weapon:        '#cf6a1f',
  armor:         '#b8651c',
  gear:          '#cf6a1f',
  // Currency / shop — amber gold
  currency:      '#c9a227',
  shop:          '#c9a227',
  // Food / consumable — lime green
  food:          '#76c442',
  consumable:    '#76c442',
  potion:        '#7cb342',
  ingredient:    '#9ccc65',
  recipes:       '#aed581',
  plants:        '#558b2f',
  // Travel / exploration — teal
  travel:        '#1abc9c',
  // Creatures — saturated green (matches "elements" green from reporter usage)
  creatures:     '#27c95a',
  // NPCs / dialogue — cyan
  npcs:          '#17b8c4',
  characters:    '#17b8c4',
  players:       '#17b8c4',
  // Journal / quests — sky blue
  journal:       '#4a90d9',
  quest:         '#3a7fb8',
  quests:        '#3a7fb8',
  // Messages / communication — bright blue (matches "description" blue from reporter usage)
  messages:      '#4a90d9',
  message:       '#4a90d9',
  'dm-whisper':  '#5dade2',
  // Spells / magic — indigo
  spell:         '#5e6cc8',
  spells:        '#5e6cc8',
  scroll:        '#7b88d3',
  component:     '#7b88d3',
  // Systems / narrative apparatus — purple (matches "screenshots" purple from reporter usage)
  systems:       '#9b59b6',
  mark:          '#8e44ad',
  reveal:        '#a569bd',
  // Cinematics / dramatic — magenta
  'dm-cinematic':'#c0488a',
  'dm-status':   '#c87cc0',
  // Character state — mustard
  character:     '#d4a040',
  // Session metadata — slate
  session:       '#7d8ca0',
  // Misc / catch-all
  other:         '#95a5a6',
  misc:          '#95a5a6',
  base:          '#95a5a6',
  custom:        '#95a5a6',
  fantasy:       '#95a5a6',
  multicolor:    '#95a5a6',
  accessibility: '#a4b0be',
  professional:  '#a4b0be',
};

// Deterministic hash → palette fallback for any category not in the
// curated map. Picks from a curated rainbow so unknown categories still
// look reasonable instead of all collapsing to grey.
const FALLBACK_PALETTE = [
  '#e74c3c', '#e67e22', '#f1c40f', '#76c442', '#27c95a',
  '#1abc9c', '#17b8c4', '#4a90d9', '#5e6cc8', '#9b59b6',
  '#c0488a', '#7d8ca0',
];
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const colorForCategory = (cat) => {
  if (CATEGORY_COLORS[cat]) return CATEGORY_COLORS[cat];
  if (!cat) return '#95a5a6';
  return FALLBACK_PALETTE[hashStr(cat) % FALLBACK_PALETTE.length];
};

function CategoryStackBar({ byCategory, total }) {
  if (!total || total === 0) return null;
  const entries = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <div style={{
        display: 'flex',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.04)',
      }}>
        {entries.map(([cat, count]) => (
          <div
            key={cat}
            title={`${cat}: ${count} (${Math.round((count / total) * 100)}%)`}
            style={{
              flexGrow: count,
              background: colorForCategory(cat),
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6, fontSize: '0.55rem', color: '#888' }}>
        {entries.slice(0, 5).map(([cat, count]) => (
          <span key={cat}>
            <span style={{ color: colorForCategory(cat) }}>●</span> {cat} {count}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActivityCard({ row, accent }) {
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 14,
      borderLeft: `4px solid ${accent}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#e8d5b7',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>
          {row.name}
        </span>
        <span style={{ fontSize: '0.6rem', color: '#888' }}>
          {row.total} action{row.total === 1 ? '' : 's'}
        </span>
        {row.lastActivityAt > 0 && (
          <span style={{ fontSize: '0.55rem', color: '#666', marginLeft: 'auto' }}>
            last {new Date(row.lastActivityAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
      }}>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Active days</div>
          <div style={{ fontSize: '1.05rem', color: accent, fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.activeDays}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sessions</div>
          <div style={{ fontSize: '1.05rem', color: accent, fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.sessions}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Per session</div>
          <div style={{ fontSize: '1.05rem', color: accent, fontWeight: 700, fontFamily: 'Georgia, serif' }}>{row.actionsPerSession}</div>
        </div>
      </div>
      {row.total > 0 && <CategoryStackBar byCategory={row.byCategory} total={row.total} />}
    </div>
  );
}

function ActivitySection() {
  const data = useUserActivity();
  if (data === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading activity…</div>;
  }
  if (data === null) return null;
  const { perPlayer, dm, topCategories, windowDays } = data;
  const hasAnyActivity = (dm.total > 0) || perPlayer.some(p => p.total > 0);
  return (
    <div>
      <div style={{
        marginBottom: 10,
        padding: '8px 12px',
        background: 'rgba(74,144,217,0.06)',
        border: '1px solid rgba(74,144,217,0.2)',
        borderRadius: 6,
        fontSize: '0.65rem',
        color: '#888',
      }}>
        Last {windowDays} days from audit log. Per-player attribution via char ownership;
        DM events aggregate (no userId on actor).
      </div>
      {!hasAnyActivity ? (
        <div style={{
          padding: 24,
          textAlign: 'center',
          color: '#666',
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(74,144,217,0.2)',
          borderRadius: 8,
          fontSize: '0.78rem',
        }}>
          No audit activity in the last {windowDays} days.
        </div>
      ) : (
        <>
          {topCategories.length > 0 && (
            <div style={{ ...PAGE_CARD_STYLE, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 8 }}>
                ⚖️ Action mix across everyone
              </div>
              <CategoryStackBar
                byCategory={Object.fromEntries(topCategories.map(c => [c.category, c.count]))}
                total={topCategories.reduce((acc, c) => acc + c.count, 0)}
              />
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}>
            {dm.total > 0 && <ActivityCard row={dm} accent="#9b59b6" />}
            {perPlayer.map((row, i) => (
              <ActivityCard key={i} row={row} accent="#4a90d9" />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// --- Anonymous page-view counters --------------------------------------

function ViewPopularitySection() {
  const data = useViewCounters();
  if (data === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading view counts…</div>;
  }
  if (data === null) return null;
  const { views, grandTotal } = data;
  if (views.length === 0) {
    return (
      <div style={{
        padding: 24,
        textAlign: 'center',
        color: '#666',
        background: 'rgba(255,255,255,0.02)',
        border: '1px dashed rgba(201,162,39,0.2)',
        borderRadius: 8,
        fontSize: '0.78rem',
      }}>
        No view counts yet. The counter starts incrementing as users navigate.
      </div>
    );
  }
  const max = Math.max(1, ...views.map(v => v.total));
  return (
    <div>
      <div style={{
        marginBottom: 10,
        padding: '8px 12px',
        background: 'rgba(39,201,90,0.06)',
        border: '1px solid rgba(39,201,90,0.2)',
        borderRadius: 6,
        fontSize: '0.65rem',
        color: '#888',
      }}>
        Anonymous — counters store {`{view, role, count}`}, no user IDs.
        Grand total: <span style={{ color: '#27c95a', fontWeight: 600 }}>{grandTotal}</span> navigations recorded.
      </div>
      <div style={{ ...PAGE_CARD_STYLE, padding: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {views.map(v => {
            const meta = getViewLabelMeta(v.view);
            const pct = (v.total / max) * 100;
            const playerPct = v.total > 0 ? (v.player / v.total) * pct : 0;
            const dmPct = v.total > 0 ? (v.dm / v.total) * pct : 0;
            return (
              <div key={v.view} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem' }}>
                <span
                  title={`${meta.description} Raw key: ${v.view}`}
                  style={{ width: 170, color: '#b0a48a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {meta.label}
                </span>
                <div style={{
                  flex: 1,
                  height: 14,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  display: 'flex',
                }}>
                  <div
                    title={`${v.player} player visit${v.player === 1 ? '' : 's'}`}
                    style={{
                      width: `${playerPct}%`,
                      background: 'linear-gradient(90deg, #4a90d966, #4a90d9)',
                    }}
                  />
                  <div
                    title={`${v.dm} DM visit${v.dm === 1 ? '' : 's'}`}
                    style={{
                      width: `${dmPct}%`,
                      background: 'linear-gradient(90deg, #9b59b666, #9b59b6)',
                    }}
                  />
                </div>
                <span style={{ width: 80, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.62rem', color: '#888' }}>
                  P {v.player} · DM {v.dm}
                </span>
                <span style={{ width: 40, textAlign: 'right', color: '#c9a227', fontWeight: 600, fontSize: '0.7rem' }}>
                  {v.total}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: '0.55rem', color: '#888' }}>
          <span><span style={{ color: '#4a90d9' }}>●</span> Player</span>
          <span><span style={{ color: '#9b59b6' }}>●</span> DM</span>
        </div>
      </div>
    </div>
  );
}

// --- Operational health (extended insights) -----------------------------

const fmtMs = (ms) => {
  if (ms == null) return '—';
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = sec / 60;
  if (min < 60) return `${min.toFixed(1)} min`;
  const hr = min / 60;
  if (hr < 48) return `${hr.toFixed(1)} hr`;
  const days = hr / 24;
  return `${days.toFixed(1)} days`;
};

// Hero strip — the four metrics the dev should see first when they
// open the dashboard. Designed to answer "what needs my attention?":
//   - Open reports (queue length)
//   - Stale for review (reporters not confirming)
//   - Avg time to address (am I responsive?)
//   - Reopen rate (am I shipping good fixes?)
function HeroStrip({ stats, insights, onNavigateToReports }) {
  const open = stats?.reports?.byStatus?.open ?? 0;
  const addressed = stats?.reports?.byStatus?.addressed ?? 0;
  const stale = insights?.staleForReview?.length ?? 0;
  const ttaMs = insights?.timeToAddress?.medianMs;
  const reopenPct = insights?.reopen?.rate != null ? Math.round(insights.reopen.rate * 100) : null;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: 10,
      marginBottom: 20,
      padding: 14,
      background: 'linear-gradient(135deg, rgba(201,162,39,0.08), rgba(155,89,182,0.05))',
      border: '1px solid rgba(201,162,39,0.25)',
      borderRadius: 10,
      boxShadow: '0 4px 18px rgba(0,0,0,0.3)',
    }}>
      <button
        onClick={onNavigateToReports || undefined}
        style={{
          display: 'flex', flexDirection: 'column', gap: 4, padding: 8,
          background: 'transparent', border: 'none',
          textAlign: 'left',
          cursor: onNavigateToReports ? 'pointer' : 'default',
          borderRight: '1px solid rgba(255,255,255,0.04)',
        }}
        title="Browse all reports →"
      >
        <span style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          Open queue
        </span>
        <span style={{
          fontSize: '2rem', color: '#ff6961', fontWeight: 700, lineHeight: 1,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>{open}</span>
        <span style={{ fontSize: '0.62rem', color: '#888' }}>
          {addressed > 0 ? `+ ${addressed} addressed` : 'reports awaiting triage'}
        </span>
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8, borderRight: '1px solid rgba(255,255,255,0.04)' }}>
        <span style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          Stale for review
        </span>
        <span style={{
          fontSize: '2rem',
          color: stale > 0 ? '#b89bdb' : '#666',
          fontWeight: 700, lineHeight: 1,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>{stale}</span>
        <span style={{ fontSize: '0.62rem', color: '#888' }}>
          addressed &gt; 7d, not confirmed
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8, borderRight: '1px solid rgba(255,255,255,0.04)' }}>
        <span style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          Time to address
        </span>
        <span style={{
          fontSize: '2rem', color: '#78dc8c', fontWeight: 700, lineHeight: 1,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>{fmtMs(ttaMs)}</span>
        <span style={{ fontSize: '0.62rem', color: '#888' }}>median (your responsiveness)</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8 }}>
        <span style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          Reopen rate
        </span>
        <span style={{
          fontSize: '2rem',
          color: reopenPct != null ? (reopenPct === 0 ? '#27c95a' : reopenPct < 20 ? '#c9a227' : '#ff6961') : '#666',
          fontWeight: 700, lineHeight: 1,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>{reopenPct != null ? `${reopenPct}%` : '—'}</span>
        <span style={{ fontSize: '0.62rem', color: '#888' }}>fixes that didn't stick</span>
      </div>
    </div>
  );
}

// Section header with a colored accent — visual cue for section role
// (action/data/trend/people). Replaces the old uniform gold heading.
function SectionHeader({ icon, label, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 12px' }}>
      <span style={{
        fontSize: '0.95rem',
        color: accent,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}>
        {icon} {label}
      </span>
      <span style={{
        flex: 1,
        height: 2,
        background: `linear-gradient(90deg, ${accent}66, transparent)`,
      }} />
    </div>
  );
}

function HealthMetricCard({ label, value, sublabel, accent = '#c9a227' }) {
  return (
    <div style={{
      ...PAGE_CARD_STYLE,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      borderLeft: `4px solid ${accent}`,
      minHeight: 88,
    }}>
      <div style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.6rem', color: accent,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontWeight: 700, lineHeight: 1.05,
      }}>
        {value}
      </div>
      {sublabel && (
        <div style={{ fontSize: '0.65rem', color: '#888' }}>{sublabel}</div>
      )}
    </div>
  );
}

// Quality / responsiveness metrics. Avg criticality + reopen rate +
// stale-for-review live in the hero strip now; this section focuses on
// fix-quality and engagement signals that complement the hero.
function OperationalHealthSection() {
  const insights = useDashboardInsights();
  if (insights === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading quality metrics…</div>;
  }
  if (insights === null) return null;
  const { timeToAddress, timeToResolve, repeatReporters } = insights;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 10,
    }}>
      <HealthMetricCard
        label="Time to address"
        value={fmtMs(timeToAddress.medianMs)}
        sublabel={timeToAddress.sampleCount > 0
          ? `median · p90 ${fmtMs(timeToAddress.p90Ms)} · n=${timeToAddress.sampleCount}`
          : 'no data yet'}
        accent="#78dc8c"
      />
      <HealthMetricCard
        label="Time to resolve"
        value={fmtMs(timeToResolve.medianMs)}
        sublabel={timeToResolve.sampleCount > 0
          ? `median · p90 ${fmtMs(timeToResolve.p90Ms)} · n=${timeToResolve.sampleCount}`
          : 'no data yet'}
        accent="#27c95a"
      />
      <HealthMetricCard
        label="Repeat reporters"
        value={repeatReporters.rate != null ? `${repeatReporters.count} / ${repeatReporters.total}` : '—'}
        sublabel={repeatReporters.rate != null
          ? `${(repeatReporters.rate * 100).toFixed(0)}% of reporters file again`
          : 'no data yet'}
        accent="#4a90d9"
      />
    </div>
  );
}

function StaleForReviewSection() {
  const insights = useDashboardInsights();
  if (insights === undefined || insights === null) return null;
  const { staleForReview } = insights;
  if (staleForReview.length === 0) return null;
  return (
    <div style={{ ...PAGE_CARD_STYLE, padding: 14, marginTop: 10 }}>
      <div style={{ fontSize: '0.78rem', color: '#b89bdb', fontWeight: 600, marginBottom: 8 }}>
        🕰️ Stale "for review" — nudge candidates
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.7rem' }}>
        {staleForReview.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 6px', background: 'rgba(184,155,219,0.06)',
            border: '1px solid rgba(184,155,219,0.15)', borderRadius: 4,
          }}>
            <span style={{ color: '#b89bdb', fontFamily: 'monospace' }}>{s.reportId}</span>
            <span style={{ color: '#888', flex: 1 }}>
              addressed {s.daysStale}d ago
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Day-of-week × hour-of-day heatmap ----------------------------------

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function InfrastructureLinksCluster() {
  return (
    <div style={{
      flex: '1 1 360px',
      minWidth: 260,
      maxWidth: 720,
      paddingLeft: 14,
      borderLeft: '1px solid rgba(201,162,39,0.18)',
    }}>
      <div style={{ fontSize: '0.68rem', color: '#c9a227', fontWeight: 700, marginBottom: 7 }}>
        Infrastructure shortcuts
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 7 }}>
        {INFRA_TOOL_LINKS.map(link => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            title={link.description}
            style={{
              padding: '6px 8px',
              background: 'rgba(74,144,217,0.1)',
              border: '1px solid rgba(74,144,217,0.28)',
              borderRadius: 5,
              color: '#4a90d9',
              textDecoration: 'none',
              fontSize: '0.66rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {link.label} -&gt;
          </a>
        ))}
      </div>
      <div style={{ fontSize: '0.56rem', color: '#666', lineHeight: 1.35, marginTop: 7 }}>
        Handy while correlating traffic spikes with backend usage, deploys, and source changes.
      </div>
    </div>
  );
}

function WeekHeatmap() {
  const insights = useDashboardInsights();
  if (insights === undefined) {
    return <div style={{ color: '#888', padding: 14, fontSize: '0.78rem' }}>Loading heatmap…</div>;
  }
  if (insights === null) return null;
  const cells = insights.weekHeatmap;
  const max = Math.max(1, ...cells);
  return (
    <div style={{ ...PAGE_CARD_STYLE, padding: 14 }}>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 4 }}>
        🌡️ Reports by day × hour
      </div>
      <div style={{ fontSize: '0.62rem', color: '#888', marginBottom: 10 }}>
        When sessions cluster — useful for capacity planning against Convex concurrency limits.
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, overflowX: 'auto', flex: '0 0 auto' }}>
        {/* Y-axis: day labels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 16 }}>
          {DOW_LABELS.map(d => (
            <div key={d} style={{
              fontSize: '0.55rem', color: '#888',
              height: 16, lineHeight: '16px', textAlign: 'right', minWidth: 26,
            }}>{d}</div>
          ))}
        </div>
        {/* Grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* X-axis: hour labels */}
          <div style={{ display: 'flex', gap: 2 }}>
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{
                width: 14, fontSize: '0.5rem', color: '#666', textAlign: 'center',
              }}>{h % 6 === 0 ? h : ''}</div>
            ))}
          </div>
          {/* Cells */}
          {DOW_LABELS.map((_, dow) => (
            <div key={dow} style={{ display: 'flex', gap: 2 }}>
              {Array.from({ length: 24 }, (_, hod) => {
                const v = cells[dow * 24 + hod] || 0;
                const intensity = v / max;
                const bg = v === 0
                  ? 'rgba(255,255,255,0.04)'
                  : `rgba(255, 105, 97, ${0.15 + intensity * 0.7})`;
                return (
                  <div
                    key={hod}
                    title={`${DOW_LABELS[dow]} ${hod}:00 — ${v} report${v === 1 ? '' : 's'}`}
                    style={{
                      width: 14, height: 16,
                      background: bg,
                      borderRadius: 2,
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
        </div>
        <InfrastructureLinksCluster />
      </div>
    </div>
  );
}

// --- Reports-per-view (friction) ----------------------------------------

function ReportsPerViewSection() {
  const insights = useDashboardInsights();
  if (insights === undefined || insights === null) return null;
  const rows = insights.reportsPerView || [];
  if (rows.length === 0) return null;
  return (
    <div style={{ ...PAGE_CARD_STYLE, padding: 14, marginTop: 10 }}>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 4 }}>
        🎯 Friction by view (reports per 100 opens)
      </div>
      <div style={{ fontSize: '0.62rem', color: '#888', marginBottom: 10 }}>
        High-friction views surface even when traffic is low. Null score = no opens recorded yet.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.slice(0, 12).map(r => {
          const meta = getViewLabelMeta(r.view);
          return (
            <div key={r.view} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem' }}>
            <span
              title={`${meta.description} Raw key: ${r.view}`}
              style={{ width: 170, color: '#b0a48a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {meta.label}
            </span>
            <span style={{ flex: 1, color: '#888', fontSize: '0.62rem' }}>
              {r.reportCount} report{r.reportCount === 1 ? '' : 's'} · {r.opens} open{r.opens === 1 ? '' : 's'}
            </span>
            <span style={{ width: 70, textAlign: 'right', color: r.frictionScore != null ? '#ff6961' : '#666', fontWeight: 600 }}>
              {r.frictionScore != null ? `${r.frictionScore.toFixed(2)} / 100` : '—'}
            </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Resolve rate per reporter ------------------------------------------

function ResolveRateSection() {
  const insights = useDashboardInsights();
  if (insights === undefined || insights === null) return null;
  const rows = insights.resolveRateByReporter || [];
  if (rows.length === 0) return null;
  return (
    <div style={{ ...PAGE_CARD_STYLE, padding: 14, marginTop: 10 }}>
      <div style={{ fontSize: '0.78rem', color: '#c9a227', fontWeight: 600, marginBottom: 4 }}>
        ⚖️ Resolve rate by reporter
      </div>
      <div style={{ fontSize: '0.62rem', color: '#888', marginBottom: 10 }}>
        % of addressed reports that the reporter has confirmed resolved. Lowest first — these are
        the testers least likely to verify your fixes.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((r, i) => {
          const pct = r.resolveRate != null ? Math.round(r.resolveRate * 100) : null;
          const accent = pct == null
            ? '#666'
            : pct >= 75 ? '#27c95a' : pct >= 40 ? '#c9a227' : '#ff6961';
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem' }}>
              <span style={{ width: 130, color: '#e8d5b7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.name}
              </span>
              <span style={{ flex: 1, color: '#888', fontSize: '0.62rem' }}>
                {r.resolved}/{r.addressed} confirmed · {r.total} total
              </span>
              <div style={{
                width: 80, height: 6, background: 'rgba(255,255,255,0.05)',
                borderRadius: 3, overflow: 'hidden',
              }}>
                {pct != null && (
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: `linear-gradient(90deg, ${accent}66, ${accent})`,
                  }} />
                )}
              </div>
              <span style={{ width: 36, textAlign: 'right', color: accent, fontWeight: 600 }}>
                {pct != null ? `${pct}%` : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Bug-reports section (the embedded ex-DevReportsPage) ---------------

function ReportCard({ report, onOpen }) {
  const parsed = safeParse(report.data);
  const tags = Array.isArray(parsed.tags) ? parsed.tags : [];
  const unread = report.hasUnreadForDm;
  const status = STATUS_INFO[report.status] || { label: report.status, color: '#888' };
  const importanceLabel = renderImportance(parsed);
  return (
    <button
      onClick={() => onOpen(report.reportId)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        gap: 6,
        padding: 14,
        background: 'linear-gradient(180deg, rgba(42,33,55,0.7) 0%, rgba(30,22,36,0.7) 100%)',
        borderRadius: 8,
        border: '1px solid rgba(201,162,39,0.22)',
        borderLeft: `4px solid ${status.color}`,
        color: '#e8d5b7',
        cursor: 'pointer',
        boxShadow: unread
          ? `0 0 0 1px ${status.color}55, 0 4px 14px rgba(0,0,0,0.45)`
          : '0 2px 10px rgba(0,0,0,0.35)',
        transition: 'transform 120ms ease-out, box-shadow 120ms ease-out',
        minHeight: 130,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 0 0 1px ${status.color}66, 0 8px 22px rgba(0,0,0,0.55)`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = unread
          ? `0 0 0 1px ${status.color}55, 0 4px 14px rgba(0,0,0,0.45)`
          : '0 2px 10px rgba(0,0,0,0.35)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: 8,
          background: `${status.color}22`,
          color: status.color,
          border: `1px solid ${status.color}55`,
          fontSize: '0.62rem',
          fontWeight: 600,
        }}>{status.label}</span>
        {unread && (
          <span title="Unread for you" style={{
            width: 8, height: 8, borderRadius: '50%',
            background: status.color,
            boxShadow: `0 0 6px ${status.color}`,
          }} />
        )}
        {importanceLabel && (
          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#c9a227', fontWeight: 600 }}>
            {importanceLabel}
          </span>
        )}
      </div>
      <div style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#e8d5b7',
        lineHeight: 1.3,
        fontFamily: 'Georgia, "Times New Roman", serif',
      }}>
        {truncateTitle(parsed.title, 10)}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {tags.slice(0, 4).map(t => (
            <span key={t} style={{
              padding: '1px 6px',
              borderRadius: 6,
              background: `${TAG_COLORS[t] || '#888'}1a`,
              color: TAG_COLORS[t] || '#888',
              fontSize: '0.58rem',
            }}>{t}</span>
          ))}
        </div>
      )}
      <div style={{ marginTop: 'auto', fontSize: '0.6rem', color: '#666' }}>
        Updated {new Date(report.lastActivityAt).toLocaleString()}
      </div>
    </button>
  );
}

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'addressed', label: 'Addressed' },
  { id: 'resolved', label: 'Resolved' },
];

const SORT_OPTIONS = [
  { id: 'importance', label: 'Importance (★)' },
  { id: 'activity',   label: 'Latest activity' },
  { id: 'created',    label: 'Created date' },
];

function BugReportsSection({ onOpenReport }) {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [tagFilter, setTagFilter] = React.useState(null);
  const [sort, setSort] = React.useState('importance');

  const all = useAllBugReports(100);
  const items = React.useMemo(() => all.results || [], [all.results]);
  const loading = all.status === 'LoadingFirstPage';
  const canLoadMore = all.status === 'CanLoadMore';

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = items.slice();
    if (q) {
      out = out.filter(r => {
        const p = safeParse(r.data);
        return (p.title || '').toLowerCase().includes(q)
          || (p.description || '').toLowerCase().includes(q);
      });
    }
    if (tagFilter) {
      out = out.filter(r => {
        const p = safeParse(r.data);
        return Array.isArray(p.tags) && p.tags.includes(tagFilter);
      });
    }
    if (statusFilter !== 'all') {
      out = out.filter(r => r.status === statusFilter);
    }
    out.sort((a, b) => {
      if (sort === 'importance') {
        const ai = effectiveImportance(safeParse(a.data));
        const bi = effectiveImportance(safeParse(b.data));
        if (ai !== bi) return bi - ai;
        return b.lastActivityAt - a.lastActivityAt;
      }
      if (sort === 'created') return b.createdAt - a.createdAt;
      return b.lastActivityAt - a.lastActivityAt;
    });
    return out;
  }, [items, search, tagFilter, statusFilter, sort]);

  return (
    <>
      <div style={{
        marginBottom: 14,
        padding: 14,
        background: 'linear-gradient(180deg, rgba(155,89,182,0.08), rgba(155,89,182,0.02))',
        border: '1px solid rgba(155,89,182,0.3)',
        borderRadius: 10,
      }}>
        <ExportPanel />
      </div>

      <div style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '10px 14px',
        background: 'linear-gradient(180deg, rgba(201,162,39,0.04), rgba(0,0,0,0.25))',
        border: '1px solid rgba(201,162,39,0.18)',
        borderRadius: 8,
        marginBottom: 14,
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search title or description…"
          style={{
            flex: '1 1 200px',
            padding: '5px 10px',
            fontSize: '0.8rem',
            background: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(201,162,39,0.25)',
            borderRadius: 5,
            color: '#e8d5b7',
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          <span style={{ fontSize: '0.62rem', color: '#888', alignSelf: 'center', marginRight: 4 }}>Status:</span>
          {STATUS_FILTERS.map(s => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              style={{
                padding: '4px 10px', fontSize: '0.7rem',
                background: statusFilter === s.id ? 'rgba(201,162,39,0.18)' : 'transparent',
                border: `1px solid ${statusFilter === s.id ? '#c9a227' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 4,
                color: statusFilter === s.id ? '#c9a227' : '#888',
                cursor: 'pointer',
                fontWeight: statusFilter === s.id ? 600 : 400,
              }}
            >{s.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.62rem', color: '#888', alignSelf: 'center', marginRight: 4 }}>Tag:</span>
          <button
            onClick={() => setTagFilter(null)}
            style={{
              padding: '4px 10px', fontSize: '0.7rem',
              background: !tagFilter ? 'rgba(201,162,39,0.18)' : 'transparent',
              border: `1px solid ${!tagFilter ? '#c9a227' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 4,
              color: !tagFilter ? '#c9a227' : '#888',
              cursor: 'pointer',
            }}
          >Any</button>
          {Object.entries(TAG_COLORS).filter(([id]) => id !== 'ui').map(([id, color]) => (
            <button
              key={id}
              onClick={() => setTagFilter(tagFilter === id ? null : id)}
              style={{
                padding: '4px 10px', fontSize: '0.7rem',
                background: tagFilter === id ? `${color}22` : 'transparent',
                border: `1px solid ${tagFilter === id ? color : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 4,
                color: tagFilter === id ? color : '#888',
                cursor: 'pointer',
                fontWeight: tagFilter === id ? 600 : 400,
              }}
            >{id}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.62rem', color: '#888' }}>Sort:</span>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: '0.72rem',
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: 4,
              color: '#e8d5b7',
            }}
          >
            {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ color: '#888', padding: 20 }}>Loading reports…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{
          padding: 32,
          textAlign: 'center',
          color: '#666',
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(201,162,39,0.2)',
          borderRadius: 8,
        }}>
          {items.length === 0
            ? 'No reports across all users yet.'
            : 'No reports match the current filters.'}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 12,
        }}>
          {filtered.map(r => (
            <ReportCard key={r.reportId} report={r} onOpen={onOpenReport} />
          ))}
        </div>
      )}

      {canLoadMore && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            onClick={() => all.loadMore(50)}
            style={{
              padding: '8px 20px',
              background: 'rgba(74,144,217,0.12)',
              border: '1px solid rgba(74,144,217,0.4)',
              borderRadius: 6,
              color: '#4a90d9',
              cursor: 'pointer',
              fontSize: '0.78rem',
            }}
          >
            Load 50 more
          </button>
        </div>
      )}
    </>
  );
}

function CollapsibleBugReportsSection({ stats, onNavigateToReports, onOpenReport }) {
  const [expanded, setExpanded] = React.useState(false);
  const totalReports = stats?.reports?.total;
  const reportSummary = totalReports != null
    ? `${totalReports} report${totalReports === 1 ? '' : 's'}`
    : 'Filter, sort, export, and open report threads';

  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls="dev-dashboard-bug-reports-panel"
        onClick={() => setExpanded(v => !v)}
        style={{
          ...PAGE_CARD_STYLE,
          width: '100%',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          color: '#e8d5b7',
          textAlign: 'left',
          cursor: 'pointer',
          borderLeft: `4px solid ${expanded ? '#c9a227' : '#7d8ca0'}`,
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{
            fontSize: '0.95rem',
            fontWeight: 700,
            color: '#c9a227',
            fontFamily: 'Georgia, "Times New Roman", serif',
          }}>
            Bug reports
          </span>
          <span style={{ fontSize: '0.7rem', color: '#888' }}>
            {expanded ? 'Full report browser is open below.' : `${reportSummary} - collapsed by default.`}
          </span>
        </span>
        <span style={{ color: '#c9a227', fontWeight: 700, fontSize: '0.82rem' }}>
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      {expanded && (
        <div id="dev-dashboard-bug-reports-panel" style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={onNavigateToReports || (() => {})}
            style={{
              ...PAGE_CARD_STYLE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '12px 16px',
              cursor: onNavigateToReports ? 'pointer' : 'default',
              width: '100%',
              color: '#e8d5b7',
              textAlign: 'left',
              marginBottom: 10,
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{
                fontSize: '0.86rem',
                fontWeight: 700,
                color: '#c9a227',
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}>
                Browse all reports
              </span>
              <span style={{ fontSize: '0.66rem', color: '#888' }}>
                Open the dedicated report browser for deeper triage.
              </span>
            </span>
            <span style={{ fontSize: '1.2rem', color: '#c9a227' }}>-&gt;</span>
          </button>
          <StaleForReviewSection />
          <BugReportsSection onOpenReport={onOpenReport} />
        </div>
      )}
    </div>
  );
}

// --- Top-level page ---------------------------------------------------

export default function DevDashboardPage({ onBack, onNavigateToReports }) {
  const dashInsights = useDashboardInsights();
  const isDev = useIsDev();
  const stats = useDevDashboardStats();
  const [openReportId, setOpenReportId] = React.useState(null);

  if (isDev === undefined) {
    return <div style={PAGE_WRAP_STYLE}><div style={PAGE_INNER_STYLE}><div style={{ color: '#888', padding: 20 }}>Loading…</div></div></div>;
  }
  if (!isDev) {
    return (
      <div style={PAGE_WRAP_STYLE}>
        <div style={PAGE_INNER_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  padding: '6px 12px', background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5,
                  color: '#888', cursor: 'pointer', fontSize: '0.78rem',
                }}
              >← Back</button>
            )}
            <h1 style={PAGE_TITLE_STYLE}>🛠️ Dev tools</h1>
          </div>
          <div style={{
            ...PAGE_CARD_STYLE,
            padding: 32,
            textAlign: 'center',
            color: '#888',
          }}>
            Dev access required. If you should have access, ask the maintainer to grant it.
          </div>
        </div>
      </div>
    );
  }

  if (openReportId) {
    return (
      <div style={PAGE_WRAP_STYLE}>
        <div style={PAGE_INNER_STYLE}>
          <ReportThread
            reportId={openReportId}
            onBack={() => setOpenReportId(null)}
            onOpenReport={(id) => setOpenReportId(id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE_WRAP_STYLE}>
      <div style={PAGE_INNER_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: '6px 12px', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5,
                color: '#888', cursor: 'pointer', fontSize: '0.78rem',
              }}
            >← Back</button>
          )}
          <h1 style={PAGE_TITLE_STYLE}>🛠️ Developer Dashboard</h1>
          <span style={{ marginLeft: 'auto', fontSize: '0.66rem', color: '#666', fontStyle: 'italic' }}>
            {stats?.generatedAt ? `Updated ${new Date(stats.generatedAt).toLocaleTimeString()}` : ''}
          </span>
        </div>

        {/* Hero strip — what needs my attention right now. */}
        <HeroStrip stats={stats} insights={dashInsights} onNavigateToReports={onNavigateToReports} />

        {false && (
          <>

        {/* Quick links — bug reports browser + stale list (action items
            that bubble up out of the hero strip into clickable surfaces). */}
        <button
          onClick={onNavigateToReports || (() => {})}
          style={{
            ...PAGE_CARD_STYLE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 18px',
            cursor: onNavigateToReports ? 'pointer' : 'default',
            width: '100%',
            color: '#e8d5b7',
            textAlign: 'left',
            transition: 'transform 120ms ease-out, box-shadow 120ms ease-out',
            marginBottom: 8,
          }}
          onMouseEnter={e => onNavigateToReports && (e.currentTarget.style.transform = 'translateY(-1px)')}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
              fontSize: '0.95rem', fontWeight: 700, color: '#c9a227',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}>
              📋 Browse all reports
            </span>
            <span style={{ fontSize: '0.7rem', color: '#888' }}>
              {stats?.reports?.total != null
                ? `${stats.reports.total} report${stats.reports.total === 1 ? '' : 's'} · filter, sort by importance, export`
                : 'Filter by status, tag, reporter; export for bulk review'}
            </span>
          </div>
          <span style={{ fontSize: '1.4rem', color: '#c9a227' }}>→</span>
        </button>
        <StaleForReviewSection />
          </>
        )}

        {/* TRENDS — temporal patterns. Blue accent (data/observation). */}
        <SectionHeader icon="📈" label="Trends" accent="#4a90d9" />
        <InsightsSection stats={stats} />
        <WeekHeatmap />

        {/* QUALITY — fix responsiveness + engagement. Green accent
            (health/positive). */}
        <SectionHeader icon="🩺" label="Fix quality" accent="#27c95a" />
        <OperationalHealthSection />

        {/* PEOPLE — per-user behavior. Purple accent (user-focused). */}
        <SectionHeader icon="👥" label="People" accent="#9b59b6" />
        <BugReporterUsageSection />
        <ResolveRateSection />
        <SystemsUsageSection />

        {/* FRICTION — where reports cluster. Orange accent (warnings). */}
        <SectionHeader icon="🎯" label="Where reports cluster" accent="#ff9f7e" />
        <ReportsPerViewSection />
        <ViewPopularitySection />

        {/* ACTIVITY — what users are doing. Cyan accent (observation). */}
        <SectionHeader icon="📊" label="Activity (last 30 days)" accent="#26c6da" />
        <ActivitySection />

        {/* AT-A-GLANCE NUMBERS — totals + averages. Gold (the legacy
            stat-card row, kept here so the more granular metrics live
            below the hero/trends/quality/people that drive action). */}
        <SectionHeader icon="📊" label="Aggregates" accent="#c9a227" />
        <StatsGrid stats={stats} insights={dashInsights} />

        {/* CONFIG — system-wide kill switches. Muted slate accent
            (admin / less-frequent action). */}
        <SectionHeader icon="🎚️" label="Configuration" accent="#7d8ca0" />
        <FeatureFlagsSection />

        <SectionHeader icon="📋" label="Bug reports" accent="#c9a227" />
        <CollapsibleBugReportsSection
          stats={stats}
          onNavigateToReports={onNavigateToReports}
          onOpenReport={(id) => setOpenReportId(id)}
        />

        <div style={{ fontSize: '0.6rem', color: '#666', marginTop: 32, textAlign: 'center', fontStyle: 'italic' }}>
          Phase-2 hooks: real "active users" via session heartbeats, time-spent analytics, cohort retention. Currently active = users with reports in last 7d.
        </div>
      </div>
    </div>
  );
}
